'use client';

import NextLink from 'next/link';
import { Children, useId } from 'react';

import cx from 'classnames';
import { A11y, Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Button, ButtonVariant } from '~/ui/components/button';
import { Column, Grid, type GridProps } from '~/ui/components/grid';
import { Icon } from '~/ui/components/icon';
import { Link } from '~/ui/components/link';
import { Breakpoint, type ColumnValue } from '~/ui/styles/grid';
import typograpyStyles from '~/ui/styles/typography.module.scss';

import { CarouselContext } from './CarouselContext';
import styles from './carousel.module.scss';
import { CarouselSize } from './types';

import './swiper.scss';

export interface CarouselProps extends Omit<GridProps, 'overflow'> {
  size: CarouselSize;
  title?: string;
  ctaType?: 'button' | 'link';
  ctaLabel?: string;
  ctaHref?: string;
  cardSpan?: ColumnValue<number>;
  children?: React.ReactNode[];
}

export function Carousel({
  size,
  title,
  ctaType = 'button',
  ctaLabel,
  ctaHref,
  cardSpan,
  children,
  className,
  ...props
}: CarouselProps) {
  const id = useId();

  let cardSize: ColumnValue<number>;
  if (cardSpan) {
    cardSize = cardSpan;
  } else {
    switch (size) {
      case CarouselSize.GRANTS:
      case CarouselSize.NEWS:
        cardSize = {
          [Breakpoint.MOBILE]: 3,
          [Breakpoint.TABLET]: 3,
          [Breakpoint.DESKTOP]: 3,
        };
        break;

      case CarouselSize.HIGHLIGHT:
        cardSize = {
          [Breakpoint.MOBILE]: 3,
          [Breakpoint.TABLET]: 2.5,
          [Breakpoint.DESKTOP]: 2,
        };
        break;
    }
  }

  return (
    <CarouselContext value={true}>
      <Grid
        render={<section />}
        aria-roledescription="carousel"
        aria-labelledby={`${id}-title`}
        className={cx(styles.root, className, {
          [styles.news]: size === CarouselSize.NEWS,
          [styles.grants]: size === CarouselSize.GRANTS,
          [styles.highlight]: size === CarouselSize.HIGHLIGHT,
        })}
        {...props}
      >
        <Column
          span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 7, [Breakpoint.DESKTOP]: 8 }}
          offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
        >
          <Grid>
            <Column span={{ [Breakpoint.DESKTOP]: 7 }} className={styles.header}>
              {title && (
                <h2 className={typograpyStyles.modulesTitle} id={`${id}-title`}>
                  {title}
                </h2>
              )}
              <div className={styles.controls}>
                <button id={`${id}-prev`}>
                  <Icon name="expanding-arrow-left" />
                </button>
                <button id={`${id}-next`}>
                  <Icon name="expanding-arrow-right" />
                </button>
              </div>
            </Column>
          </Grid>
          <Grid
            render={
              <Swiper
                modules={[A11y, Keyboard, Navigation]}
                a11y={{
                  prevSlideMessage: 'Previous',
                  nextSlideMessage: 'Next',
                  itemRoleDescriptionMessage: 'slide',
                  slideLabelMessage: '',
                }}
                keyboard
                navigation={{
                  // We have to use an attribute selector (e.g. [id="xyz"]) instead of
                  //   an id selector (e.g. #xyz) because the value provided by useId()
                  //   is not valid CSS.
                  prevEl: `[id="${id}-prev"]`,
                  nextEl: `[id="${id}-next"]`,
                }}
                slidesPerView="auto"
              />
            }
            className={styles.swiper}
          >
            {Children.map(children, (child, index) => (
              <Slide index={index} cardSpan={cardSize} key={index}>
                {child}
              </Slide>
            ))}
          </Grid>

          {!!ctaLabel && !!ctaHref && (
            <div className={styles.cta}>
              {ctaType === 'button' ? (
                <Button variant={ButtonVariant.SECONDARY} render={<NextLink href={ctaHref} />}>
                  {ctaLabel}
                </Button>
              ) : (
                <Link href={ctaHref} withIcon>
                  {ctaLabel}
                </Link>
              )}
            </div>
          )}
        </Column>
      </Grid>
    </CarouselContext>
  );
}

// Swiper uses `displayName` to determine whether the children are slides or not. This is a hack to
//  allow us to have <Column /> slides: https://github.com/nolimits4web/swiper/issues/4413#issuecomment-1021387492
function Slide({
  index,
  cardSpan,
  children,
}: {
  index: number;
  cardSpan: ColumnValue<number>;
  children: React.ReactNode;
}) {
  return (
    <Column
      span={cardSpan}
      className={styles.slide}
      style={{
        ['--shade' as never]: `${5 * [0, 1, 2, 1][index % 4]}%`,
      }}
      render={<SwiperSlide />}
    >
      {children}
    </Column>
  );
}
Slide.displayName = 'SwiperSlide';
